import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { app } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import {
  collection,
  query,
  where,
  getFirestore,
  doc,
  getDocs,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { LuMail, LuLock, LuArrowRight, LuLoaderCircle  } from "react-icons/lu";

/* --- ANIMAÇÕES --- */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* --- ESTILOS PADRONIZADOS (Glassmorphism) --- */
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #3b82f6;
  background-image: radial-gradient(#76a9fc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 1rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  width: 100%;
  max-width: 420px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;

  @media (min-width: 640px) {
    padding: 2.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
`;

/* Inputs com Ícones */
const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  padding-left: 2.75rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: white;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: none;
  background-color: ${props => props.disabled ? '#93c5fd' : '#2563eb'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
  margin-top: 1.5rem;

  &:hover {
    background-color: ${props => props.disabled ? '#93c5fd' : '#1d4ed8'};
    transform: ${props => props.disabled ? 'none' : 'scale(0.99)'};
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #9ca3af;
  font-size: 0.85rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
  span { padding: 0 1rem; }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background-color: white;
  color: #374151;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }

  img { width: 20px; height: 20px; }
`;

const FooterLink = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: #6b7280;

  a {
    color: #2563eb;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const SpinnerIcon = styled(LuLoaderCircle )`
  animation: ${spin} 1s linear infinite;
`;

/* ====== COMPONENTE ====== */
export default function Login() {
  const auth = getAuth(app);
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Função auxiliar para verificar o status e redirecionar
  const checkStatusAndRedirect = async (uid) => {
    // 1. Verifica se é cliente (Bloqueio)
    const clienteRef = doc(db, "cliente", uid);
    const clienteSnap = await getDoc(clienteRef);
    if (clienteSnap.exists()) {
      alert("Esta conta é de cliente. Use o app de agendamento.");
      await auth.signOut();
      return;
    }

    // 2. Verifica se é Proprietário (busca barbearia criada por ele)
    const barbeariaQuery = query(
      collection(db, "barbearia"),
      where("criadorUid", "==", uid)
    );
    const barbeariaSnap = await getDocs(barbeariaQuery);

    // Se não encontrou barbearia criada por ele...
    if (barbeariaSnap.empty) {
      // Verifica se ele é APENAS um barbeiro convidado (não dono)
      // Se não tiver doc em "leads" e não tiver barbearia, assume barbeiro comum
      const leadRef = doc(db, "leads", uid);
      const leadSnap = await getDoc(leadRef);
      
      if (!leadSnap.exists()) {
        navigate("/"); // Barbeiro convidado vai direto pro painel
        return;
      }
      
      // Se tem lead mas não tem barbearia -> Cadastro incompleto
      navigate("/cadastro-barbearia");
      return;
    }

    // 3. Se é Proprietário, verifica em qual etapa parou
    const barbeariaDoc = barbeariaSnap.docs[0];
    const infos = barbeariaDoc.data().SituacaoCadastral || {};

    // Verifica ambas as chaves possíveis (singular/plural) para evitar erro
    const statusServico = infos.statusServico === "ok";
    const statusBarbeiro = (infos.statusBarbeiro === "ok" || infos.statusBarbeiros === "ok");

    if (!statusServico) {
      navigate("/cadastro-servico");
    } else if (!statusBarbeiro) {
      navigate("/cadastro-barbeiro");
    } else {
      navigate("/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await checkStatusAndRedirect(userCredential.user.uid);
    } catch (err) {
      console.error("Erro login:", err);
      alert("Email ou senha incorretos.");
      setIsLoading(false); // Só para loading se der erro
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Salva/Atualiza Lead se necessário (mesma lógica do cadastro)
      const leadRef = doc(db, "leads", user.uid);
      const leadSnap = await getDoc(leadRef);
      
      if (!leadSnap.exists()) {
         await setDoc(leadRef, {
            uid: user.uid,
            nome: user.displayName || "",
            email: user.email,
            telefone: user.phoneNumber || "",
            criadoEm: serverTimestamp(),
         }, { merge: true });
      }

      await checkStatusAndRedirect(user.uid);
    } catch (err) {
      console.error("Erro Google:", err);
      alert("Erro ao entrar com Google.");
    }
  };

  return (
    <Container>
      <img src="https://dash.bigode.app/assets/logo-negativa.png" style={{width: '200px', marginBottom: '30px'}} />
      <Card>
        <Header>
          <Title>Bem-vindo de volta!</Title>
          <Subtitle>Acesse sua conta para gerenciar.</Subtitle>
        </Header>

        <form onSubmit={handleLogin}>
          <InputGroup>
            <InputIcon><LuMail /></InputIcon>
            <StyledInput
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputIcon><LuLock /></InputIcon>
            <StyledInput
              type="password"
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? <><SpinnerIcon /> Entrando...</> : <><LuArrowRight /> Entrar</>}
          </Button>
        </form>

        <Divider><span>ou</span></Divider>

        <GoogleButton type="button" onClick={handleGoogleLogin}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
          />
          Entrar com Google
        </GoogleButton>

        <FooterLink>
          Ainda não tem conta? <Link to="/cadastro">Cadastre-se</Link>
        </FooterLink>
      </Card>
    </Container>
  );
}